import { Checkbox, CircularProgress, Tooltip } from "@mui/material";
import { Link } from "react-router-dom";
import InfoIcon from "@mui/icons-material/Info";
import useTableOptions from "Hooks/useTableOptions";
import { useEffect, useState } from "react";
import MUITable from "Components/UI/Tables/MUITable";
import ToggleSelectBtn from "Components/UI/Tables/ToggeSelectBtn";
import RefreshBtn from "Components/UI/Tables/RefreshBtn";
import { price2StringPrice } from "utils/priceformatter";
import { getDefaultServiceLabelById } from "utils/labels";
import { LoadingButton } from "@mui/lab";
import ConfirmModal from "./ConfirmModal";
import { usePopupNotificationContext } from "Context/NotificationPopup";
import { useInvoices } from "Context/Invoices";

const createTableRows = (invoices) => {
  return invoices.map((i) => {
    return [
      i.CustomerAccountId,
      i.CustomerAccountId,
      { invoiceNo: i.InvoiceNumber, invoiceId: i.InvoiceID }, //Faktura nr
      i.OCR, // OCR
      {
        endCustomerId: i.EndCustomerId,
        endCustomerNumber: i.CustomerNumber,
      }, // KundNr
      { customerName: i.CustomerName, newCustomer: i.EndCustomerId }, // Kundnamn
      i.ClientName, // Klientnamn
      i.ClientNumber, // Klientnr
      price2StringPrice(i.Amount), // Belopp
      i.Description,
      i.InvoiceDate, // Fakturadatum
      i.CalcFfdDate, // Förfallodatum
      i.ClosedDate, // Stängd
      i.CalcFfdDate && i.ClosedDate
        ? Math.ceil(
            Math.abs(new Date(i.CalcFfdDate) - new Date(i.ClosedDate)) /
              (1000 * 60 * 60 * 24),
          )
        : null, // Dagar försening
      getDefaultServiceLabelById(i.InvoiceService), // Förvald tjänst
      i.ExportFileId, // Fil Id
      i.BgMaxFileId,
      i.CreateTime, // Skapad
    ];
  });
};

const ProcessInterestInvoices = () => {
  const { handleColumnShow, isColDisplayed } =
    useTableOptions("InvoicesMainTable");

  const {
    interestAccounts,
    fetchAllInterestInvoices,
    activateInterestInvoices,
    deleteInterestInvoices,
  } = useInvoices();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [disableCheckboxes, setDisableCheckboxes] = useState(true);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState(new Set());
  const [rowCount, setRowCount] = useState(30);
  const [invoices, setInvoices] = useState([]);
  const { openSuccess, openError } = usePopupNotificationContext();

  const checkAll = (force = false) => {
    if (!force && selectedInvoiceIds.size === invoices.length) {
      setSelectedInvoiceIds(new Set());
      return;
    }
    const newSelectedInvoiceIds = new Set(selectedInvoiceIds);
    for (const invoice of invoices) {
      if (selectedInvoiceIds.has(invoice.CustomerAccountId)) {
        continue;
      }
      newSelectedInvoiceIds.add(invoice.CustomerAccountId);
    }
    setSelectedInvoiceIds(newSelectedInvoiceIds);
  };

  const handleCheckBoxChange = (value) => {
    const newSelectedInvoiceIds = new Set(selectedInvoiceIds);
    if (selectedInvoiceIds.has(value)) {
      newSelectedInvoiceIds.delete(value);
    } else {
      newSelectedInvoiceIds.add(value);
    }
    setSelectedInvoiceIds(newSelectedInvoiceIds);
  };

  const isItemSelected = (value) => {
    return selectedInvoiceIds.has(value);
  };

  useEffect(() => {
    setSelectedInvoiceIds(new Set());
    setInvoices(interestAccounts);
    setRows(createTableRows(interestAccounts));
  }, [interestAccounts]);

  const columns = [
    {
      name: "",
      options: {
        filter: false,
        customHeadRender: (columnMeta) => (
          <th
            key={columnMeta.index}
            style={{ borderBottom: "1px solid lightgray" }}
          >
            <Checkbox
              checked={selectedInvoiceIds.size === invoices.length}
              onClick={() => checkAll()}
            />
          </th>
        ),
        customBodyRender: (value) => {
          return (
            <tr style={{ display: "flex", justifyContent: "center" }}>
              <Checkbox
                checked={isItemSelected(value)}
                onChange={() => handleCheckBoxChange(value)}
              />
            </tr>
          );
        },
      },
    },
    {
      name: "Id",
      options: {
        display: isColDisplayed("Id"),
        customBodyRender: (value) => {
          return <Link to={`/endcustomersaccounts/${value}`}>{value}</Link>;
        },
      },
    },
    {
      name: "Fakturanummer",
      options: {
        display: isColDisplayed("Fakturanummer"),
        empty: false,
        customBodyRender: (value) => {
          return (
            <Link to={`/invoices/${value.invoiceId}`}>{value.invoiceNo}</Link>
          );
        },
      },
    },
    {
      name: "OCR",
      options: {
        display: isColDisplayed("OCR"),
      },
    },
    {
      name: "Kundsnummer",
      options: {
        display: isColDisplayed("Kundsnummer"),
        customBodyRender: (value) => {
          return (
            <Link to={`/endcustomers/${value.endCustomerId}`}>
              {value.endCustomerNumber}
            </Link>
          );
        },
      },
    },
    {
      name: "Kundnamn",
      options: {
        display: isColDisplayed("Kundnamn"),
        customBodyRender: (value) => {
          return (
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                minWidth: "150px",
              }}
            >
              {value.newCustomer ? (
                <Tooltip title="Ny kund">
                  <InfoIcon fontSize="small" color="info" />
                </Tooltip>
              ) : null}
              {value.customerName}
            </span>
          );
        },
      },
    },
    {
      name: "Klientnamn",
      options: {
        display: isColDisplayed("Klientnamn"),
      },
    },
    {
      name: "Klientnr",
      options: {
        display: isColDisplayed("Klientnr"),
      },
    },
    {
      name: "Belopp",
      options: {
        display: isColDisplayed("Belopp"),
        customBodyRender: (value) => {
          const number = Number(value.split(" ").join(""));
          return (
            <span style={{ color: number < 0 ? "red" : null }}>{value}</span>
          );
        },
      },
    },
    {
      name: "Beskrivning",
      options: {
        display: isColDisplayed("Beskrivning"),
      },
    },
    {
      name: "Fakturadatum",
      options: {
        display: isColDisplayed("Fakturadatum"),
      },
    },
    {
      name: "Förfallodatum",
      options: {
        display: isColDisplayed("Förfallodatum"),
      },
    },
    {
      name: "Stängd",
      options: {
        display: isColDisplayed("Stängd"),
      },
    },
    {
      name: "Dagar försening",
      options: {
        display: isColDisplayed("Dagar försening"),
      },
    },
    {
      name: "Tjänst",
      options: {
        display: isColDisplayed("Tjänst"),
      },
    },
    {
      name: "Exportfil ID",
      options: {
        display: isColDisplayed("Exportfil ID"),
        empty: false,
        customBodyRender: (value) => {
          return <Link to={`/files/${value}`}>{value}</Link>;
        },
      },
    },
    {
      name: "BgMax ID",
      options: {
        display: isColDisplayed("BgMax ID"),
        empty: false,
        customBodyRender: (value) => {
          return <Link to={`/files/${value}`}>{value}</Link>;
        },
      },
    },
    {
      name: "CreateTime",
      label: "Skapad",
      options: {
        display: isColDisplayed("CreateTime"),
      },
    },
  ];

  const options = {
    onViewColumnsChange: (changedColumn, action) => {
      handleColumnShow(changedColumn, action);
    },
    customToolbar: () => {
      return (
        <>
          <ToggleSelectBtn
            onClick={() => setDisableCheckboxes(!disableCheckboxes)}
          />
          <RefreshBtn />
        </>
      );
    },
    rowsPerPage: rowCount,
    rowsPerPageOptions: [10, 30, 100],
    selectableRowsHeader: true,
    onChangeRowsPerPage: (numberOfRows) => setRowCount(numberOfRows),
    selectableRowsHideCheckboxes: disableCheckboxes,
    textLabels: {
      body: {
        noMatch: loading ? <CircularProgress /> : "",
      },
    },
    filter: false,
    downloadOptions: {
      separator: ";",
      filename: "all_clients.csv",
    },
    onDownload: (buildHead, buildBody, columns, data) => {
      return "\uFEFF" + buildHead(columns) + buildBody(data);
    },
  };

  const handleOnConfirm = async () => {
    setLoading(true);
    const invoiceIds = Array.from(selectedInvoiceIds);
    const res = await activateInterestInvoices(invoiceIds);
    setSelectedInvoiceIds(new Set());
    if (res?.Status === "ok") {
      fetchAllInterestInvoices();
      openSuccess(res?.Message, 3000);
    } else {
      openError("Något gick fel", 3000);
    }
    setLoading(false);
  };

  const handleOnDelete = async () => {
    setLoading(true);
    const invoiceIds = Array.from(selectedInvoiceIds);
    const res = await deleteInterestInvoices(invoiceIds);
    setSelectedInvoiceIds(new Set());
    if (res?.Status === "ok") {
      fetchAllInterestInvoices();
      openSuccess(res?.Message, 3000);
    } else {
      openError("Något gick fel", 3000);
    }
    setLoading(false);
    setDeleteModalOpen(false);
  };

  useEffect(() => {
    fetchAllInterestInvoices();
  }, []);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        margin: "20px",
        gap: "20px",
      }}
    >
      <MUITable
        options={options}
        title={
          <h2 style={{ color: "lightblack" }}>
            Räntefakturor redo för utskick
          </h2>
        }
        data={rows}
        columns={columns}
      />
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <LoadingButton
          variant="contained"
          loading={loading}
          disabled={rows.length === 0 || selectedInvoiceIds.size === 0}
          onClick={() => setDeleteModalOpen(true)}
        >
          Ta bort
        </LoadingButton>
        <LoadingButton
          variant="contained"
          loading={loading}
          disabled={rows.length === 0 || selectedInvoiceIds.size === 0}
          onClick={handleOnConfirm}
        >
          Bokför och skriv ut
        </LoadingButton>
      </div>
      <ConfirmModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title={"Ta bort"}
      >
        {`Vill du verkligen ta bort valda räntefakturor?`}
        <div
          style={{
            display: "flex",
            marginTop: "50px",
            justifyContent: "flex-end",
            gap: "10px",
          }}
        >
          <LoadingButton
            loading={modalLoading}
            onClick={handleOnDelete}
            variant="contained"
          >
            Bekräfta
          </LoadingButton>
          <LoadingButton onClick={() => setDeleteModalOpen(false)}>
            Avbryt
          </LoadingButton>
        </div>
      </ConfirmModal>
    </div>
  );
};

export default ProcessInterestInvoices;
